import { useCallback, useEffect, useMemo } from 'react';
import { debounce } from 'lodash';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { updatePreferences, updateBalancePreference, getPreferences, } from 'actions/user';
const normalize = (preferences) => {
    const pushPreferences = [];
    const emailPreferences = [];
    const feedPreferences = [];
    Object.entries(preferences).forEach((entry) => {
        const [key] = entry;
        if (key.includes('alerts') && key.includes('.push')) {
            pushPreferences.push(entry);
        }
        else if (key.includes('alerts') && key.includes('.email')) {
            emailPreferences.push(entry);
        }
        else if (key.includes('feed')) {
            feedPreferences.push(entry);
        }
    });
    return {
        pushPreferences: pushPreferences.reduce((value, item) => ({
            ...value,
            [item[0]]: item[1].value,
        }), {}),
        emailPreferences: emailPreferences.reduce((value, item) => ({
            ...value,
            [item[0]]: item[1].value,
        }), {}),
        feedPreferences: feedPreferences.reduce((value, item) => ({
            ...value,
            [item[0]]: item[1].value,
        }), {}),
    };
};
const getBalancePreferenceSetting = (preferences) => {
    const dailyBalanceKey = 'alerts.daily_balance.push';
    const preference = preferences[dailyBalanceKey];
    if (!preference) {
        return { value: false };
    }
    if (typeof preference === 'boolean') {
        return { value: preference };
    }
    return {
        value: preference.value,
        info: preference.info,
    };
};
export const useNotificationsPreferences = (shouldFetchPreferences) => {
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (shouldFetchPreferences) {
            dispatch(getPreferences());
        }
    }, [shouldFetchPreferences]);
    const notificationPreferences = useAppSelector((store) => store.user.preferences);
    const isUpdating = useAppSelector((store) => !!store.unpersistedUtils.updatingPreferencesTimestamp);
    const { pushPreferences, emailPreferences, feedPreferences } = useMemo(() => normalize(notificationPreferences), [notificationPreferences]);
    const balancePreference = useMemo(() => getBalancePreferenceSetting(notificationPreferences), [notificationPreferences]);
    const debouncedUpdatePreferences = useMemo(() => debounce((preferences, timestamp) => dispatch(updatePreferences(preferences, timestamp)), 500), []);
    const updatePreferencesCallback = useCallback((preferences) => {
        const timestamp = new Date().getTime();
        dispatch({
            type: 'UPDATE_PREFERENCES_REQUEST_TIMESTAMP',
            extra: { timestamp },
        });
        debouncedUpdatePreferences(preferences, timestamp);
    }, [debouncedUpdatePreferences]);
    const debouncedUpdateBalancePreference = useMemo(() => debounce((enabledAccountIds, disabledAccountIds, timestamp) => dispatch(updateBalancePreference(enabledAccountIds, disabledAccountIds, timestamp)), 500), []);
    const updateBalancePreferencesCallback = useCallback((enabledAccountIds, disabledAccountIds) => {
        const timestamp = new Date().getTime();
        dispatch({
            type: 'UPDATE_PREFERENCES_REQUEST_TIMESTAMP',
            extra: { timestamp },
        });
        debouncedUpdateBalancePreference(enabledAccountIds, disabledAccountIds, timestamp);
    }, [debouncedUpdateBalancePreference]);
    return {
        updatePreferences: updatePreferencesCallback,
        updateBalancePreference: updateBalancePreferencesCallback,
        pushNormalizedNotificationPreferences: pushPreferences,
        emailNormalizedNotificationPreferences: emailPreferences,
        feedPreferences,
        balancePreference,
        isUpdating,
    };
};
